import type * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import type { Invoice, TenantId } from '@etiquetachef/types';
import { recordManualPaymentCallable } from '@/lib/callables';
import { trackEvents } from '@/lib/events';
import { gatewayErrorMessage, gatewayOwnedInvoiceWarning } from './gatewayErrors';
import { serverNow } from '@etiquetachef/ui-shared/online';

type Props = {
  open: boolean;
  tenantId: TenantId;
  invoice: Invoice | null;
  onClose: () => void;
  onSuccess?: () => void;
};

/** Epoch ms → `YYYY-MM-DDTHH:mm` no fuso do navegador (valor do datetime-local). */
function toLocalDateTimeInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(d.getFullYear())}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Diálogo "Marcar fatura como paga" (baixa manual) do admin.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200.
 *
 * Contrato: chama `recordManualPayment` (centavos). Permissão no servidor:
 * `requireTenantOperator` com `staffRoles: ADMIN_PERMISSIONS.recordManualPayment`
 * (ROOT/FINANCE — `docs/permissions.md`, Billing) ou ADMIN do CRM
 * (`crmAdminOnly`). Fatura do gateway
 * (`isGatewayOwnedInvoice`) → aviso e confirmar desabilitado; o servidor recusa
 * do mesmo jeito (`failed-precondition`, `details.code: gateway_owned_invoice`),
 * e essa recusa sai traduzida (`gatewayErrorMessage`) num `Alert` dentro do
 * modal (#1021).
 *
 * Usado por: `InvoiceActions` (em `routes/InvoiceDetailPage.tsx`, rota
 * `/billing/invoices/:tenantId/:invoiceId`) e `routes/InvoicesPage.tsx`
 * (rotas `/billing/invoices` e `/billing/invoices/:tenantId`, menu Billing →
 * Faturas), admin web. Spec: `docs/screens/billing-invoices.md`. Teste:
 * `GatewayInvoiceDialogs.test.tsx`.
 *
 * Dados — input (`lib/callables.ts`), exemplo anonimizado:
 *   { "tenantId": "tenant-1", "invoiceId": "inv-1",
 *     "requestId": "<uuid por abertura>",
 *     "amount": 1997,            // CENTAVOS (o input mostra 19.97 em unidades)
 *     "currency": "BRL",         // da fatura; a tela não deixa trocar
 *     "paid_at": 1760000000000,  // epoch ms, fuso do navegador do operador
 *     "notes": "PIX manual" }    // opcional; vazio vira undefined
 * `paid_at` vem do relógio/entrada do operador — é dado declarado, não
 * decisão de prazo.
 *
 * Falhas conhecidas:
 *  - 2026-10-07 12:55 +0200 (#795, 1º nível item 3): clique duplo gravava o
 *    pagamento duas vezes — sem id de pedido e com o bloqueio só no estado.
 *
 * Histórico:
 *  - 2026-10-03 18:22 +0200 — (#795, BIL-4) aviso + bloqueio para fatura do
 *    gateway; recusa do servidor traduzida pelo `details.code`.
 *  - 2026-10-07 12:55 +0200 — (#795) `requestId` por abertura + trava síncrona.
 *  - 2026-10-10 14:40 +0200 — (#798) cabeçalho padrão (#796).
 *  - 2026-10-25 14:29 +0100 — (#1021) erro da ação dentro do modal (o toast ficava atrás do overlay).
 */
export function RecordManualPaymentDialog({
  open,
  tenantId,
  invoice,
  onClose,
  onSuccess,
}: Props): React.JSX.Element | null {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [paidAt, setPaidAt] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // #795 (1º nível item 3, 2026-10-07 12:55 +0200): um id por ABERTURA do
  // diálogo, reusado em todo retry — o servidor grava um payment só por id.
  // O ref de envio fecha o clique duplo antes do re-render do `submitting`
  // (o `setState` é assíncrono e o 2º clique passava).
  const requestIdRef = useRef('');
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (open && invoice) {
      requestIdRef.current = crypto.randomUUID();
      // CR-01: invoice.amount_due em CENTS, input do user em unidades.
      setAmount((invoice.amount_due / 100).toFixed(2));
      setPaidAt(toLocalDateTimeInput(serverNow()));
      setNotes('');
      setError(null);
    }
  }, [open, invoice]);

  if (!invoice) return null;

  // #795 (BIL-4, 2026-10-03 18:22 +0200): baixa manual numa fatura do
  // Stripe/Asaas só mudava o espelho — o gateway seguia cobrando (cobrança
  // dupla). O servidor recusa; aqui o operador é avisado antes de preencher.
  const gatewayWarning = gatewayOwnedInvoiceWarning(invoice, 'record_payment', t);

  /** Limpa o erro e devolve o controle ao pai. */
  function close(): void {
    setError(null);
    onClose();
  }

  /** Valida, converte reais→centavos e grava a baixa com o `requestId` da abertura. */
  async function handleSubmit(): Promise<void> {
    if (!invoice || gatewayWarning !== null || inFlightRef.current) return;
    const amountNum = Number(amount);
    if (!Number.isFinite(amountNum) || amountNum < 0) {
      setError(t('tenantDetail.actions.amountInvalid'));
      return;
    }
    const paidAtMs = paidAt === '' ? serverNow() : new Date(paidAt).getTime();
    if (!Number.isFinite(paidAtMs)) {
      setError(t('tenantDetail.actions.dateInvalid'));
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      // CR-01: callable espera amount em CENTS. Input do user está em
      // unidades decimais (ex.: 19.97). Converter na borda.
      await recordManualPaymentCallable({
        tenantId,
        invoiceId: invoice.id,
        requestId: requestIdRef.current,
        amount: Math.round(amountNum * 100),
        currency: invoice.currency,
        paid_at: paidAtMs,
        notes: notes.trim() === '' ? undefined : notes.trim(),
      });
      trackEvents.invoiceMarkedPaid(tenantId, invoice.id);
      onSuccess?.();
      close();
    } catch (err) {
      setError(
        gatewayErrorMessage(err, t) ??
          (err instanceof Error ? err.message : t('tenantDetail.actions.errorGeneric')),
      );
    } finally {
      inFlightRef.current = false;
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('billing.recordPayment.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              loading={submitting}
              disabled={gatewayWarning !== null}
            >
              {t('billing.recordPayment.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box variant="p" color="text-body-secondary">
          {t('billing.recordPayment.description', { invoice: invoice.invoice_number })}
        </Box>
        {gatewayWarning !== null && <Alert type="warning">{gatewayWarning}</Alert>}

        <FormField label={t('billing.recordPayment.amountLabel', { currency: invoice.currency })}>
          <Input
            type="number"
            step={0.01}
            value={amount}
            onChange={({ detail }): void => {
              setAmount(detail.value);
            }}
            disabled={submitting}
          />
        </FormField>

        <FormField label={t('billing.recordPayment.paidAtLabel')}>
          {/* Cloudscape não tem Input datetime-local; usa o nativo (DatePicker +
              TimeInput seria 2 campos e mudaria o modelo de estado). */}
          {/* eslint-disable-next-line no-restricted-syntax */}
          <input
            type="datetime-local"
            value={paidAt}
            disabled={submitting}
            onChange={(e): void => {
              setPaidAt(e.target.value);
            }}
            style={{
              width: '100%',
              padding: '4px 8px',
              borderRadius: 8,
              border: '1px solid var(--color-border-input-default, #7d8998)',
              background: 'var(--color-background-input-default, transparent)',
              color: 'inherit',
              font: 'inherit',
            }}
          />
        </FormField>

        <FormField label={t('billing.recordPayment.notesLabel')}>
          <Textarea
            value={notes}
            onChange={({ detail }): void => {
              setNotes(detail.value);
            }}
            rows={2}
            disabled={submitting}
            placeholder={t('billing.recordPayment.notesPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
